import { formatDateSk, formatDateTimeSecondsSk } from "app/utils/dateFormat";

const normalizeType = (raw) =>
  String(raw || "")
    .trim()
    .toLowerCase();

const pickFirstNumber = (...candidates) => {
  for (const c of candidates) {
    if (c === null || c === undefined || c === "") continue;
    const n = typeof c === "number" ? c : Number(c);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const resolveTicketValidity = (ticket) => {
  const data = ticket?.ticket ?? ticket;
  const validityType =
    data?.validityType ??
    data?.validity_type ??
    data?.ticketSettings?.validityType ??
    data?.productSnapshot?.ticketSettings?.validityType ??
    "";

  const durationMinutes = pickFirstNumber(
    data?.durationMinutes,
    data?.duration_minutes,
    data?.validityDurationMinutes,
    data?.validity_duration_minutes,
    data?.validityDuration,
    data?.validity_duration,
    data?.ticketSettings?.durationMinutes,
    data?.productSnapshot?.ticketSettings?.durationMinutes,
  );

  const validFrom = data?.validFrom ?? data?.valid_from ?? null;
  const validTo = data?.validTo ?? data?.valid_to ?? null;

  return {
    type: normalizeType(validityType),
    durationMinutes:
      durationMinutes != null ? Math.max(0, Math.floor(durationMinutes)) : null,
    validFrom,
    validTo,
  };
};

/**
 * Maps ticket validity data to compact UI text mirroring print rules.
 * Returns up to 2 lines (primary + optional secondary) and optional range.
 */
export const getTicketValidityDisplay = (
  t,
  ticket,
  { withDateTime = true } = {},
) => {
  const { type, durationMinutes, validFrom, validTo } =
    resolveTicketValidity(ticket);
  const fmt = withDateTime ? formatDateTimeSecondsSk : formatDateSk;

  if (type === "single_entry") {
    return {
      primary: t("tickets.validity.ui.singleEntry.primary", {
        defaultValue: "Jednorazový vstup",
      }),
      secondary: t("tickets.validity.ui.singleEntry.secondary", {
        defaultValue: "Po prvom vstupe lístok prestáva platiť.",
      }),
      range: null,
    };
  }

  if (type === "from_first_use") {
    const total = durationMinutes ?? 0;
    const h = Math.floor(total / 60);
    const m = total % 60;
    return {
      primary: t("tickets.validity.ui.fromFirstUse.primary", {
        defaultValue: `Platnosť: ${h} h ${m} min od prvého vstupu`,
      }),
      secondary: t("tickets.validity.ui.fromFirstUse.secondary", {
        defaultValue: "Platnosť sa začne počítať pri prvom úspešnom vstupe.",
      }),
      range: null,
    };
  }

  if (type === "calendar_day") {
    const fromStr = validFrom ? fmt(validFrom, "") : "";
    const toStr = validTo ? fmt(validTo, "") : "";
    return {
      primary: t("tickets.validity.ui.calendarDay.primary", {
        defaultValue: "Platnosť: kalendárny deň",
      }),
      secondary: null,
      range: fromStr && toStr ? `${fromStr} – ${toStr}` : null,
    };
  }

  // Fallback: show a date range if provided (e.g. time_range), otherwise show the raw type.
  const fromStr = validFrom ? fmt(validFrom, "") : "";
  const toStr = validTo ? fmt(validTo, "") : "";
  const range = fromStr && toStr ? `${fromStr} – ${toStr}` : null;
  return {
    primary: type
      ? t(`tickets.validity.ui.fallback.${type}`, { defaultValue: type })
      : "—",
    secondary: null,
    range,
  };
};
